import { useContext, useMemo, useState } from 'react';
import { CARD_GRADIENTS } from '../../../assets/constants/cardStyles';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { CheckIcon } from '../../../assets/icons/icons';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import BottomSheet from '../../shared/BottomSheet/BottomSheet';
import './HistoryCardSheet.css';

function normalizeCardIds(cardIds) {
  return Array.from(new Set((cardIds || []).filter(Boolean)));
}

function SelectionMark({ selected }) {
  return (
    <span
      className={
        selected
          ? 'history-card-sheet__selection history-card-sheet__selection--active'
          : 'history-card-sheet__selection'
      }
      aria-hidden="true"
    >
      {selected ? <CheckIcon className="history-card-sheet__check" /> : null}
    </span>
  );
}

function CardThumbnail({ card }) {
  const background = CARD_GRADIENTS[card.style] || CARD_GRADIENTS.sunset;

  return (
    <span
      className="history-card-sheet__thumbnail"
      style={{ background }}
      aria-hidden="true"
    >
      <span className="history-card-sheet__thumbnail-shine" />
      <span className="history-card-sheet__thumbnail-number">
        {card.lastFour || card.last4 || ''}
      </span>
    </span>
  );
}

function HistoryCardSheet({ cards = [], value = [], onApply, onClose }) {
  const { common, history } = useContext(TranslationContext);
  const availableCards = useMemo(
    () => cards.filter((card) => card?.id && (card.lastFour || card.last4)),
    [cards]
  );
  const [draftCardIds, setDraftCardIds] = useState(() => normalizeCardIds(value));
  const selectedSet = useMemo(() => new Set(draftCardIds), [draftCardIds]);
  const isAllCardsSelected = draftCardIds.length === 0;

  const selectAllCards = () => {
    if (!isAllCardsSelected) {
      triggerImpactHaptic('light');
      setDraftCardIds([]);
    }
  };

  const toggleCard = (cardId) => {
    triggerImpactHaptic('light');
    setDraftCardIds((current) => {
      if (current.length === 0) {
        return [cardId];
      }

      const isSelected = current.includes(cardId);
      const next = isSelected
        ? current.filter((id) => id !== cardId)
        : [...current, cardId];

      return next.length > 0 ? next : [];
    });
  };

  const handleApply = () => {
    triggerImpactHaptic('medium');
    onApply(normalizeCardIds(draftCardIds));
  };

  return (
    <BottomSheet
      className="history-card-sheet"
      panelClassName="history-card-sheet__panel"
      titleId="history-card-sheet-title"
      onClose={onClose}
    >
      <div className="history-card-sheet__layout">
        <header className="history-card-sheet__header">
          <div className="history-card-sheet__heading">
            <h2 id="history-card-sheet-title" className="history-card-sheet__title">
              {history.selectCardsTitle}
            </h2>
            <p className="history-card-sheet__subtitle">
              {history.selectCardsSubtitle}
            </p>
          </div>
          <button
            type="button"
            className="history-card-sheet__reset"
            disabled={isAllCardsSelected}
            onClick={selectAllCards}
          >
            {history.resetAll}
          </button>
        </header>

        <div
          className="history-card-sheet__list"
          role="group"
          aria-label={history.cardsFilter}
        >
          <button
            type="button"
            className={
              isAllCardsSelected
                ? 'history-card-sheet__item history-card-sheet__item--selected'
                : 'history-card-sheet__item'
            }
            aria-pressed={isAllCardsSelected}
            onClick={selectAllCards}
          >
            <span className="history-card-sheet__all-icon" aria-hidden="true">
              <span />
              <span />
            </span>
            <span className="history-card-sheet__item-copy">
              <span className="history-card-sheet__item-title">{history.allCards}</span>
              <span className="history-card-sheet__item-caption">
                {history.allCardsCaption}
              </span>
            </span>
            <SelectionMark selected={isAllCardsSelected} />
          </button>

          {availableCards.map((card) => {
            const selected = selectedSet.has(card.id);
            const lastFour = card.lastFour || card.last4;
            const label = card.label || card.name || history.unnamedCard;

            return (
              <button
                key={card.id}
                type="button"
                className={
                  selected
                    ? 'history-card-sheet__item history-card-sheet__item--selected'
                    : 'history-card-sheet__item'
                }
                aria-pressed={selected}
                onClick={() => toggleCard(card.id)}
              >
                <CardThumbnail card={card} />
                <span className="history-card-sheet__item-copy">
                  <span className="history-card-sheet__item-title">{label}</span>
                  <span className="history-card-sheet__item-caption">•••• {lastFour}</span>
                </span>
                <SelectionMark selected={selected} />
              </button>
            );
          })}

          {availableCards.length === 0 ? (
            <div className="history-card-sheet__empty" role="status">
              <span className="history-card-sheet__empty-title">
                {history.noCardsForFilterTitle}
              </span>
              <span className="history-card-sheet__empty-text">
                {history.noCardsForFilterText}
              </span>
            </div>
          ) : null}
        </div>

        <div className="history-card-sheet__action-wrap">
          <button
            type="button"
            className="history-card-sheet__action"
            onClick={handleApply}
          >
            {history.showTransactions || common.apply}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default HistoryCardSheet;
